// KAI-Drawer (Designkonzept §5a): rechte Schublade (~400 px), die über dem
// Workspace aufschiebt — keine feste Chat-Spalte. Kontextgebunden: der Kopf
// zeigt, woran gearbeitet wird (z. B. „Lernziel 2 · <Kurztext>"), der
// Workspace bleibt darunter sichtbar.
//
// Fachlich derselbe Dienst wie DidaktikChat (POST /chat/message, Kontext
// „course": der Server lädt den persistierten Kurs über course_id selbst).
// Light-Surface-Hybrid (2026-08-14): helle Drawer-Fläche, dunkle Schrift.
// Vorschläge (proposal) werden wie im
// Audit-Report angewendet: Items mit id = PATCH auf Bestand, ohne id =
// Neuanlage; entfernt wird nie etwas.

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  api,
  APIError,
  isQuotaError,
  type ChatMessage,
  type ChatProposal,
  type ContentLang,
  type SuggestedAT,
  type SuggestedILO,
  type SuggestedTLA,
} from "@/api/client";
import CreditCost from "@/components/CreditCost";
import QuotaExhaustedCard from "@/components/QuotaExhaustedCard";

const TARGET_LABEL_KEYS: Record<ChatProposal["target"], string> = {
  ilos: "chat.targetIlos",
  tlas: "chat.targetTlas",
  ats: "chat.targetAts",
};

function previewTexts(p: ChatProposal): string[] {
  return p.items
    .map((it) => ("text" in it ? it.text : it.description))
    .filter((s): s is string => !!s)
    .slice(0, 3);
}

export default function KaiDrawer({
  open,
  contextLabel,
  entitySummary,
  onAttach,
  attachLabel,
  attachedLabel,
  courseId,
  language,
  onClose,
  onApplied,
}: {
  open: boolean;
  contextLabel: string;
  /** Inhalt der aktivierten Karte — wird der ersten Nachricht unsichtbar
   *  vorangestellt, damit KAI karten-spezifisch statt allgemein antwortet
   *  (Franz 2026-08-15: KAI kannte nur den Kurs, nicht die Karte). */
  entitySummary?: string;
  /** Wenn gesetzt: Assistenten-Antworten bekommen „An Methode anhängen". */
  onAttach?: (content: string) => Promise<void>;
  attachLabel?: string;
  attachedLabel?: string;
  courseId: string;
  language: ContentLang;
  onClose: () => void;
  onApplied: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  // Kartenwechsel = neues Gespräch: History/Proposal leeren, sonst ginge der
  // Kontext der alten Karte still mit (und die neue Karte bekäme keinen).
  const ctxKey = `${contextLabel}|${entitySummary ?? ""}`;
  useEffect(() => {
    setMessages([]);
    setProposal(null);
    setError(null);
    setApplyErrors([]);
    setAttachedIdx(new Set());
  }, [ctxKey]);
  const [proposal, setProposal] = useState<ChatProposal | null>(null);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [quota, setQuota] = useState<unknown>(null);
  const [applyErrors, setApplyErrors] = useState<string[]>([]);
  const [attaching, setAttaching] = useState(false);
  const [attachedIdx, setAttachedIdx] = useState<Set<number>>(new Set());

  async function attach(idx: number, content: string) {
    if (!onAttach || attaching || attachedIdx.has(idx)) return;
    setAttaching(true);
    try {
      await onAttach(content);
      setAttachedIdx((prev) => new Set(prev).add(idx));
    } catch {
      setError(t("chat.failed"));
    } finally {
      setAttaching(false);
    }
  }

  async function send() {
    const text = input.trim();
    if (!text || busy) return;
    const next: ChatMessage[] = [...messages, { role: "user", content: text }];
    setMessages(next);
    setInput("");
    setBusy(true);
    setError(null);
    // Karten-Kontext: der API-Payload der ERSTEN Nachricht trägt den Inhalt
    // der aktivierten Karte (unsichtbar für den Nutzer, bleibt in der
    // History und wirkt damit auch auf Folgefragen). Angezeigt wird nur
    // der eigentliche Fragetext.
    const apiNext =
      messages.length === 0 && entitySummary
        ? [
            {
              role: "user" as const,
              content:
                `[Arbeitskontext — die Karte, an der ich gerade arbeite: ${contextLabel}]\n` +
                `${entitySummary}\n\n` +
                `[Meine Frage — beziehe dich konkret auf diese Karte im Kurskontext, nicht allgemein]\n${text}`,
            },
          ]
        : next;
    try {
      const res = await api.chatMessage({
        messages: apiNext,
        context: "course",
        course_id: courseId,
        language,
      });
      setMessages([...next, { role: "assistant", content: res.reply }]);
      setProposal(res.proposal ?? null);
    } catch (e) {
      if (isQuotaError(e)) {
        setQuota(e.data);
      } else if (e instanceof APIError && e.status === 429) {
        setError(t("chat.rateLimited"));
      } else if (e instanceof APIError) {
        setError(e.detail);
      } else {
        setError(t("chat.failed"));
      }
    } finally {
      setBusy(false);
    }
  }

  // Proposal anwenden (gleiche Semantik wie AuditReport.applyCourseChatProposal).
  async function apply(p: ChatProposal) {
    if (applying) return;
    setApplying(true);
    const errs: string[] = [];
    for (const item of p.items) {
      try {
        if (p.target === "ilos") {
          const it = item as SuggestedILO;
          const payload = {
            text: it.text,
            action_verb: it.action_verb,
            bender_depth: it.bender_depth,
          };
          if (it.id) await api.updateILO(courseId, it.id, payload);
          else await api.createILO(courseId, payload);
        } else if (p.target === "tlas") {
          const it = item as SuggestedTLA;
          const payload = {
            type: it.type,
            description: it.description,
            duration_minutes: it.duration_minutes ?? undefined,
            modality: it.modality ?? undefined,
            linked_ilo_ids: it.linked_ilo_ids ?? [],
          };
          if (it.id) await api.updateTLA(courseId, it.id, payload);
          else await api.createTLA(courseId, payload);
        } else {
          const it = item as SuggestedAT;
          const payload = {
            type: it.type,
            description: it.description,
            format: it.format ?? undefined,
            duration_minutes: it.duration_minutes ?? undefined,
            linked_ilo_ids: it.linked_ilo_ids ?? [],
          };
          if (it.id) await api.updateAT(courseId, it.id, payload);
          else await api.createAT(courseId, payload);
        }
      } catch (e) {
        errs.push(e instanceof APIError ? e.detail : String(e));
      }
    }
    setProposal(null);
    setApplyErrors(errs);
    setApplying(false);
    await onApplied();
  }

  const preview = proposal ? previewTexts(proposal) : [];

  return (
    <aside
      className={`fixed inset-y-0 right-0 z-40 flex w-[400px] max-w-[92vw] flex-col border-l border-ws-surface-border bg-ws-card text-ws-surface-ink shadow-[-12px_0_40px_rgba(0,0,0,.5)] transition-transform duration-[250ms] ease-out ${
        open ? "translate-x-0" : "translate-x-[102%]"
      }`}
      aria-hidden={!open}
    >
      <div className="relative border-b border-ws-surface-border px-4.5 py-3.5" style={{ padding: "14px 18px" }}>
        <h3 className="text-base font-semibold tracking-[-0.01em]">✦ KAI</h3>
        <span className="mt-0.5 block pr-6 text-xs font-semibold uppercase tracking-[0.12em] text-ws-surface-muted">
          {contextLabel}
        </span>
        <button
          onClick={onClose}
          className="absolute right-3.5 top-2.5 text-base text-ws-surface-muted transition hover:text-ws-surface-ink"
          aria-label={t("common.close")}
        >
          ✕
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-4.5 py-4" style={{ padding: "16px 18px" }}>
        {messages.length === 0 && (
          <p className="text-[15px] leading-relaxed text-ws-surface-muted">{t("chat.intro")}</p>
        )}
        {messages.map((m, i) => (
          <div key={i} className="mb-3">
            <div
              className={`max-w-[92%] rounded-[10px] px-4 py-3 text-[17px] leading-relaxed whitespace-pre-wrap ${
                m.role === "user"
                  ? "ml-auto border border-[rgba(77,124,254,.35)] bg-[rgba(77,124,254,.12)]"
                  : "border border-ws-surface-border bg-white"
              }`}
            >
              {m.content}
            </div>
            {/* Anhang speichern (Franz 2026-08-15: gute KAI-Antworten wie
                Fallberichte sollen an der Methode hängen bleiben). */}
            {m.role === "assistant" && onAttach && (
              <button
                onClick={() => void attach(i, m.content)}
                disabled={attaching}
                className="ml-1 mt-1 rounded-md border border-ws-surface-border px-2.5 py-1 text-xs font-semibold text-ws-surface-muted transition hover:border-[rgba(77,124,254,.45)] hover:text-ca-surface-accent disabled:opacity-50"
              >
                {attachedIdx.has(i) ? attachedLabel : attachLabel}
              </button>
            )}
          </div>
        ))}
        {busy && <p className="mb-3 text-sm text-ws-surface-muted">{t("chat.sending")}</p>}

        {proposal && (
          <div className="mb-3 rounded-[10px] border border-[rgba(77,124,254,.4)] bg-white p-3.5">
            <p className="mb-1 text-xs font-semibold uppercase tracking-[0.08em] text-ca-surface-accent">
              {t("chat.proposalTitle", { target: t(TARGET_LABEL_KEYS[proposal.target]) })}
            </p>
            {proposal.rationale && (
              <p className="mb-2 text-[15px] text-ws-surface-ink">{proposal.rationale}</p>
            )}
            {preview.length > 0 && (
              <ul className="mb-3 list-disc space-y-1 pl-5 text-sm text-ws-surface-muted">
                {preview.map((s, i) => (
                  <li key={i}>{s}</li>
                ))}
                {proposal.items.length > preview.length && (
                  <li>{t("chat.proposalMore", { count: proposal.items.length - preview.length })}</li>
                )}
              </ul>
            )}
            <div className="flex gap-2">
              <button
                onClick={() => void apply(proposal)}
                disabled={applying}
                className="rounded-md border border-[rgba(77,124,254,.4)] px-3 py-1.5 text-sm font-semibold text-ca-surface-accent transition hover:bg-[rgba(77,124,254,.12)] disabled:opacity-50"
              >
                {applying ? t("chat.sending") : `${t("chat.apply")} →`}
              </button>
              <button
                onClick={() => setProposal(null)}
                className="rounded-md border border-ws-surface-border px-3 py-1.5 text-sm font-semibold text-ws-surface-muted transition hover:text-ws-surface-ink"
              >
                {t("chat.discard")}
              </button>
            </div>
            <p className="mt-2 text-xs italic text-ws-surface-muted">
              {t("chat.applyHintCourse")}
            </p>
          </div>
        )}
        {applyErrors.length > 0 && (
          <p className="mb-3 text-sm text-verdict-red">{applyErrors.join(" · ")}</p>
        )}
        {quota !== null && (
          <div className="mb-3">
            <QuotaExhaustedCard detail={quota} onDismiss={() => setQuota(null)} />
          </div>
        )}
        {error && <p className="mb-3 text-sm text-verdict-red">{error}</p>}
      </div>

      <div className="flex gap-2 border-t border-ws-surface-border" style={{ padding: "12px 18px" }}>
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") void send();
          }}
          placeholder={t("chat.placeholder")}
          disabled={busy}
          className="flex-1 rounded-lg border border-ws-surface-border bg-white px-3 py-2.5 text-base text-ws-surface-ink placeholder:text-ws-surface-muted outline-none focus:border-[rgba(77,124,254,.5)] focus:shadow-ws-glow disabled:opacity-50"
        />
        <span className="self-center text-xs">
          <CreditCost feature="chat" className="text-ws-surface-muted" />
        </span>
        <button
          onClick={() => void send()}
          disabled={!input.trim() || busy}
          className="rounded-lg border border-[rgba(77,124,254,.4)] px-3.5 text-sm font-semibold text-ca-surface-accent transition hover:bg-[rgba(77,124,254,.12)] disabled:opacity-40"
        >
          {t("chat.send")}
        </button>
      </div>
    </aside>
  );
}
